import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Input,
  Row,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { TriangleAlert } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { FRAMEWORKS, MAX_STRUCTURE_SCORE, sectionTiming, type AnswerFramework } from '@evaltick/shared';

import { ROUTES } from '@/routes';
import { usePageBackLink } from '@/hooks';
import { ResultsExportModal } from '@/components/ResultsExportModal';
import {
  AdminService,
  type AssessmentAdminNote,
  type AssessmentResults,
  type AssessmentResultResponse,
} from '@/services/adminService';
import { formatDurationClock, formatGuestLabel, formatUserName } from '@/utils/formatters';
import { cn, SCORE_LABEL_COLORS } from '@/utils';
import { DimensionScores } from './AdminHtsSection';
import styles from './AdminSessionResults.module.scss';

// AssessmentResultResponse types questionType as a plain string, so widen the
// framework lookup to string keys rather than casting each access to QuestionType.
const FRAMEWORK_BY_QUESTION_TYPE: Record<string, AnswerFramework> = FRAMEWORKS;

const StructureComponentScores: React.FC<{
  scores: AssessmentResultResponse['structureComponentScores'];
}> = ({ scores }) => {
  if (!scores || scores.length === 0) return null;
  return (
    <div className={styles.dimensions}>
      {scores.map((s) => {
        const timing = sectionTiming(s);
        return (
          <div key={s.key} className={styles.dimension}>
            <div className={styles.dimensionHeader}>
              <Typography.Text strong>{s.key}</Typography.Text>
              <span className={styles.dimensionMeta}>
                {timing && (
                  <Tag>~{formatDurationClock(timing.ms)}{timing.estimated ? ' est.' : ''}</Tag>
                )}
                <Tag>{s.score} / {MAX_STRUCTURE_SCORE}</Tag>
              </span>
            </div>
            <Typography.Text type="secondary" className={styles.preserveLines}>{s.rationale}</Typography.Text>
          </div>
        );
      })}
    </div>
  );
};
const ResponseCard: React.FC<{
  response: AssessmentResultResponse;
  index: number;
  maxStructureScore: number;
  competencyWeight: number;
  structureWeight: number;
}> = ({ response, index, maxStructureScore, competencyWeight, structureWeight }) => {
  const showFormula =
    response.overallScore !== null &&
    response.competencyScore !== null &&
    response.structureScore !== null;

  return (
    <div className={styles.responseCard}>
      <div className={styles.responseHeader}>
        <Typography.Text strong>Question {index + 1} — {response.questionType}</Typography.Text>
        {response.isCarriedOver && <Tag>From previous session</Tag>}
        {response.reviewFlagged && (
          <Badge
            status="error"
            text={<Typography.Text type="danger">Flagged</Typography.Text>}
          />
        )}
      </div>

      {response.questionText && (
        <Typography.Text type="secondary" className={styles.questionText}>
          {response.questionText}
        </Typography.Text>
      )}

      <div className={styles.scoreRow}>
        {response.overallScore !== null && (
          <Statistic title="Overall" value={response.overallScore.toFixed(1)} suffix="/ 100" />
        )}
        {response.competencyScore !== null && (
          <Statistic title={`Competency (${Math.round(competencyWeight * 100)}%)`} value={response.competencyScore.toFixed(1)} suffix="/ 100" />
        )}
        {response.structureScore !== null && (
          <Statistic title={`Structure (${Math.round(structureWeight * 100)}%)`} value={response.structureScore} suffix={`/ ${maxStructureScore}`} />
        )}
      </div>

      {showFormula && (
        <Typography.Text type="secondary" className={styles.formula}>
          {response.overallScore!.toFixed(1)} = {response.competencyScore!.toFixed(1)} × {Math.round(competencyWeight * 100)}% + ({response.structureScore!}/{maxStructureScore}) × {Math.round(structureWeight * 100)}%
        </Typography.Text>
      )}

      {response.reviewReasons.length > 0 && (
        <div className={styles.reviewReasons}>
          <TriangleAlert className={styles.warningIcon} style={{ width: '1em', height: '1em' }} />
          {response.reviewReasons.map((r, i) => (
            <Typography.Text key={i} type="danger" className={styles.reviewReason}>
              {r}
            </Typography.Text>
          ))}
        </div>
      )}

      <Collapse
        ghost
        size="small"
        defaultActiveKey={response.dimensionScores?.length ? ['dimensions', 'rationale'] : ['rationale']}
        items={[
          {
            key: 'answer',
            label: 'Answer',
            children: <Typography.Paragraph className={styles.preserveLines}>{response.answer}</Typography.Paragraph>,
          },
          {
            key: 'rationale',
            label: 'Evaluation rationale',
            children: <Typography.Paragraph className={styles.preserveLines}>{response.rationale}</Typography.Paragraph>,
          },
          ...(response.dimensionScores?.length
            ? [{
                key: 'dimensions',
                label: 'Dimension scores',
                children: <DimensionScores scores={response.dimensionScores} />,
              }]
            : []),
          ...(response.structureRationale || response.structureComponentScores?.length
            ? [{
                key: 'structureComponents',
                label: `Structure breakdown (${FRAMEWORK_BY_QUESTION_TYPE[response.questionType]?.name ?? 'STARR/CARL'})`,
                children: (
                  <>
                    {response.structureRationale && (
                      <Typography.Paragraph type="secondary" className={styles.preserveLines}>{response.structureRationale}</Typography.Paragraph>
                    )}
                    <StructureComponentScores scores={response.structureComponentScores} />
                  </>
                ),
              }]
            : []),
        ]}
      />
    </div>
  );
};

interface ReviewerNoteCardProps {
  results: AssessmentResults;
  onSaved: (note: AssessmentAdminNote) => void;
}

const ReviewerNoteCard: React.FC<ReviewerNoteCardProps> = ({ results, onSaved }) => {
  const { getToken } = useAuth();
  const { notification } = App.useApp();
  const [draft, setDraft] = useState(results.adminNote ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      onSaved(await new AdminService(token).updateAssessmentAdminNote(results.id, draft));
      notification.success({ message: 'Reviewer note saved' });
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to save reviewer note' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Reviewer note"
      className={cn(styles.competencyCard, styles.adminNoteCard)}
      extra={
        <Button onClick={handleSave} loading={saving} disabled={draft.trim() === (results.adminNote ?? '')}>
          Save note
        </Button>
      }
    >
      <Typography.Text type="secondary">Never shown to the candidate.</Typography.Text>
      <Input.TextArea
        className={styles.adminNoteInput}
        autoSize={{ minRows: 2, maxRows: 6 }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      {results.adminNoteUpdatedAt && (
        <Typography.Text type="secondary" className={styles.scoringHint}>
          Last updated by {results.adminNoteUpdatedByName ?? 'an admin'} on{' '}
          {new Date(results.adminNoteUpdatedAt).toLocaleString()}
        </Typography.Text>
      )}
    </Card>
  );
};

const SUMMARY_POLL_INTERVAL_MS = 2_000;
// ~60s — must outlast the API's 30s retry wait (SUMMARY_GENERATION_TIMEOUT_MS),
// or polling stops just as a failed summary is retried.
const SUMMARY_POLL_MAX_ATTEMPTS = 30;

export const AdminSessionResults: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);

  const fetchResults = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const data = await new AdminService(token).getAssessmentResults(assessmentId);
      setResults(data);
    } catch {
      notification.error({ message: 'Failed to load assessment results' });
    } finally {
      setLoading(false);
    }
  }, [assessmentId, getToken, notification]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  usePageBackLink(() => navigate(ROUTES.ADMIN_ASSESSMENTS), 'Back to Results');

  // The summary generates in the background after completion — poll until it
  // lands, giving up after SUMMARY_POLL_MAX_ATTEMPTS.
  const [summaryPollGaveUpFor, setSummaryPollGaveUpFor] = useState<string | null>(null);
  const awaitingSummary = results !== null && results.summaryNarrative === null;
  useEffect(() => {
    if (!awaitingSummary || !assessmentId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const token = await getToken();
        if (token) {
          const data = await new AdminService(token).getAssessmentResults(assessmentId);
          if (cancelled) return;
          if (data.summaryNarrative !== null) {
            setResults((prev) => prev && { ...prev, summaryNarrative: data.summaryNarrative });
            return;
          }
        }
      } catch (err) {
        console.error('Polling for session summary failed:', err);
      }
      if (cancelled) return;
      if (attempts >= SUMMARY_POLL_MAX_ATTEMPTS) {
        setSummaryPollGaveUpFor(assessmentId);
        return;
      }
      timer = setTimeout(poll, SUMMARY_POLL_INTERVAL_MS);
    };
    timer = setTimeout(poll, SUMMARY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [awaitingSummary, assessmentId, getToken]);
  const summaryLoading = awaitingSummary && summaryPollGaveUpFor !== assessmentId;

  if (loading) {
    return (
      <div className={styles.page}>
        <Typography.Text type="secondary">Loading...</Typography.Text>
      </div>
    );
  }

  if (!results) return null;

  const isGuest = results.accessCodeTags !== null;
  const userName = results.accessCodeTags
    ? formatGuestLabel(results.accessCodeTags)
    : formatUserName(results.user.firstName, results.user.lastName) || results.user.email;
  const hasAnyFlag = results.competencyBreakdown.some((c) => c.reviewFlagged);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Button onClick={() => setExportOpen(true)}>Export PDF</Button>
      </div>

      <div className={styles.meta}>
        <Typography.Title level={2} className={styles.title}>
          {userName}
        </Typography.Title>
        <Typography.Text type="secondary" className={styles.pageSub}>
          {results.interviewTemplate.name}
          {!isGuest && <> &middot; {results.user.email}</>}
          {results.completedAt && (
            <> &middot; Completed {new Date(results.completedAt).toLocaleDateString()}</>
          )}
        </Typography.Text>
        {hasAnyFlag && (
          <Tag icon={<TriangleAlert style={{ width: '1em', height: '1em' }} />} color="error" className={styles.flagTag}>
            Review flagged
          </Tag>
        )}
      </div>

      <ReviewerNoteCard results={results} onSaved={(note) => setResults((prev) => prev && { ...prev, ...note })} />

      <Card className={styles.headlineCard}>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title="Overall Score"
              value={results.totalScore?.toFixed(1) ?? '—'}
              suffix={results.totalScore !== null ? '/ 100' : ''}
            />
            {results.label && (
              <Tag color={SCORE_LABEL_COLORS[results.label]} className={styles.labelTag}>
                {results.label}
              </Tag>
            )}
          </Col>
          <Col xs={24} sm={16}>
            <Row gutter={[16, 8]}>
              {results.competencyBreakdown.map((c) => (
                <Col key={c.competencyId} xs={24} sm={8}>
                  <Statistic
                    title={c.competencyName}
                    value={c.averageScore?.toFixed(1) ?? '—'}
                    suffix={c.averageScore !== null ? '/ 100' : ''}
                  />
                  {c.label && (
                    <Tag color={SCORE_LABEL_COLORS[c.label]}>{c.label}</Tag>
                  )}
                  {c.reviewFlagged && <Badge status="error" text="Flagged" />}
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Card>

      <Card size="small" className={styles.methodologyCard}>
        <Row gutter={[16, 8]} align="middle">
          <Col xs={24} sm={12}>
            <Typography.Text type="secondary" className={styles.formulaHeadline}>
              Score formula: Overall = Competency × {Math.round(results.scoreBlendConfig.competencyWeight * 100)}% + (Structure / {results.scoreBlendConfig.maxStructureScore}) × {Math.round(results.scoreBlendConfig.structureWeight * 100)}%
            </Typography.Text>
          </Col>
          <Col xs={24} sm={12}>
            <Row gutter={[16, 0]}>
              <Col xs={8}>
                <Statistic
                  title="Competency weight"
                  value={results.scoreBlendConfig.competencyWeight * 100}
                  suffix="%"
                />
              </Col>
              <Col xs={8}>
                <Statistic
                  title="Structure weight"
                  value={results.scoreBlendConfig.structureWeight * 100}
                  suffix="%"
                />
              </Col>
              <Col xs={8}>
                <Statistic
                  title="Max structure"
                  value={results.scoreBlendConfig.maxStructureScore}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card title="Session Summary" size="small" className={styles.summaryCard}>
        {summaryLoading ? (
          <div className={styles.summaryLoading}>
            <Spin size="small" />
            <Typography.Text type="secondary">Generating session summary…</Typography.Text>
          </div>
        ) : results.summaryNarrative ? (
          <Typography.Paragraph className={styles.preserveLines}>{results.summaryNarrative}</Typography.Paragraph>
        ) : (
          <Typography.Text type="secondary">Not yet generated.</Typography.Text>
        )}
      </Card>

      {results.competencyBreakdown.map((competency) => (
        <Card
          key={competency.competencyId}
          title={
            <span>
              {competency.competencyName}
              {competency.reviewFlagged && (
                <Tag color="error" className={styles.competencyFlag}>Flagged</Tag>
              )}
            </span>
          }
          className={styles.competencyCard}
        >
          {competency.responses.map((response, i) => (
            <ResponseCard
              key={response.id}
              response={response}
              index={i}
              maxStructureScore={results.scoreBlendConfig.maxStructureScore}
              competencyWeight={results.scoreBlendConfig.competencyWeight}
              structureWeight={results.scoreBlendConfig.structureWeight}
            />
          ))}
        </Card>
      ))}

      <ResultsExportModal
        open={exportOpen}
        selection={{ assessmentIds: [results.id], htsSubmissionIds: [] }}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
};
